import React, { useState, useRef, useEffect } from 'react';
import { Download, RefreshCw, ChevronDown } from 'lucide-react';
import { apiClient } from '@rti/shared';
import { useAuth } from '@/contexts/AuthContext';
import { elementToPdfText, htmlToPdfText } from '@/utils/pdfText';

const ACC_PDF_CACHE_VERSION = 'v4';

interface SnapshotExportButtonProps {
  resinType: 'PE' | 'PP' | 'PS' | 'PVC';
  selectedDate: string;
  commentaryType: 'preliminary' | 'final';
  disabled?: boolean;
}

interface StatCard {
  title: string;
  value: string;
  change: string;
  isPositive: boolean;
  isNegative: boolean;
}

const SnapshotExportButton: React.FC<SnapshotExportButtonProps> = ({
  resinType,
  selectedDate,
  commentaryType,
  disabled = false,
}) => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDropdown) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDropdown]);

  const formatDateForTitle = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(`${dateStr}T00:00:00Z`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  // e.g. "2026-01-28" → "Jan-26"
  const formatMonthLabel = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(`${dateStr}T00:00:00Z`);
    return date.toLocaleDateString('en-US', {
      year: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    });
  };

  // e.g. selectedDate "2026-01-28" → "Dec-Chg" (previous month)
  const formatPrevMonthChgLabel = (dateStr: string): string => {
    if (!dateStr) return 'Prev-Chg';
    const date = new Date(`${dateStr}T00:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() - 1);
    return (
      date.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }) +
      '-Chg'
    );
  };

  const captureCanvasAsImage = (canvasId: string): string | null => {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
    if (!canvas) return null;
    try {
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  };

  // Extract stat card data: title, current value, and change from previous month
  const getStatCards = (): StatCard[] => {
    const cards: StatCard[] = [];
    const statElements = document.querySelectorAll('[data-testid="stat-card"]');

    statElements.forEach((el) => {
      const titleEl = el.querySelector('[data-testid="stat-title"]');
      const valueEl = el.querySelector('[data-testid="stat-value"]');
      const title = titleEl?.textContent?.trim() || '';

      // Value: read only the direct text nodes (skips the change-indicator span)
      let value = '';
      if (valueEl) {
        Array.from(valueEl.childNodes).forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            value += node.textContent || '';
          }
        });
        value = value.trim();
      }

      const outerSpan = valueEl?.querySelector('span');
      const isNegative = outerSpan?.classList.contains('text-rs-red') ?? false;
      const isPositive = outerSpan?.classList.contains('text-rs-dark-green') ?? false;

      // Inner span holds the sign-stripped change text
      const innerSpan = outerSpan?.querySelector('span');
      const rawChange = innerSpan?.textContent?.trim() || '';

      let change = rawChange;
      if (isNegative && rawChange) change = `-${rawChange}`;
      else if (isPositive && rawChange) change = `+${rawChange}`;

      if (title) cards.push({ title, value, change, isPositive, isNegative });
    });

    return cards;
  };

  const getChartCanvasIds = (): string[] => {
    switch (resinType) {
      case 'PE':
        return ['peCumulativeChart', 'peProdSalesChart', 'hdpeProdSalesChart', 'ldpeProdSalesChart', 'lldpeProdSalesChart'];
      case 'PP':
        return ['ppDemandChart', 'ppDaysSupplyChart', 'ppProductionChart', 'ppInventoryChart', 'ppImportsChart', 'ppOperatingChart'];
      case 'PS':
        return ['psCumulativeChart', 'psSupplyDemand', 'SMExports', 'BZImports', 'BDImports'];
      case 'PVC':
        return ['pvcCumulativeChart', 'pvcProductionChart', 'pvcGlobalExport', 'DomesticSales'];
      default:
        return [];
    }
  };

  // For each chart canvas, navigate to its parent section and extract title + commentary.
  // PE/PS/PVC: section > div.h-[380px] > canvas  (need to go up 2 levels)
  // PP:        section > canvas directly           (need to go up 1 level)
  const getChartCommentaryPairs = (): { canvasId: string; title: string; text: string }[] => {
    const pairs: { canvasId: string; title: string; text: string }[] = [];
    const allChartIds = getChartCanvasIds();

    allChartIds.forEach((canvasId) => {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      const directParent = canvas.parentElement;
      // If the canvas's direct parent has an h4, it IS the section (PP style).
      // Otherwise go one level up (PE/PS/PVC style where canvas lives in div.h-[380px]).
      const sectionDiv = directParent?.querySelector('h4')
        ? directParent
        : directParent?.parentElement;

      const title = sectionDiv?.querySelector('h4')?.textContent?.trim() || '';
      const commentaryEl = sectionDiv?.querySelector('.whitespace-pre-line');
      const text = elementToPdfText(commentaryEl ?? null);

      pairs.push({ canvasId, title, text });
    });

    return pairs;
  };

  const generatePDF = async () => {
    if (!selectedDate || disabled) return;
    setIsGenerating(true);

    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 7.5;
      const contentWidth = pageWidth - margin * 2;
      const gutter = 5;

      const bgColor: [number, number, number] = [25, 26, 31];
      const greenColor: [number, number, number] = [124, 252, 0];
      const textColor: [number, number, number] = [222, 231, 217];
      const headerRowColor: [number, number, number] = [40, 42, 50];
      const evenRowColor: [number, number, number] = [30, 32, 38];
      const oddRowColor: [number, number, number] = [27, 28, 34];
      const redColor: [number, number, number] = [255, 90, 90];
      const jetLightColor: [number, number, number] = [58, 72, 52];

      // Pre-load logo once as a data URL so it can be used synchronously on every page
      let logoDataUrl: string | null = null;
      try {
        const logoResponse = await fetch('/logos/PrimaryRTi/White.png');
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          logoDataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(logoBlob);
          });
        }
      } catch {}

      let yPos = margin;

      const drawBackground = () => {
        doc.setFillColor(...bgColor);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      // Standard page header: green title, date, preliminary/final badge, logo, green divider
      const drawPageHeader = () => {
        doc.setTextColor(...greenColor);
        doc.setFontSize(23);
        doc.setFont('helvetica', 'normal');
        doc.text(`${resinType} SUPPLY/DEMAND`, margin, yPos + 6);

        doc.setTextColor(...textColor);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        const dateText = formatDateForTitle(selectedDate);
        doc.text(dateText, margin, yPos + 14);

        doc.setFontSize(12);
        doc.setTextColor(...greenColor);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `- ${commentaryType.charAt(0).toUpperCase() + commentaryType.slice(1)}`,
          margin + doc.getTextWidth(dateText) + 2,
          yPos + 14,
        );

        if (logoDataUrl) {
          doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 75, yPos + 1, 75, 16);
        }

        yPos += 19;

        doc.setDrawColor(...greenColor);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
      };

      const addNewPage = () => {
        doc.addPage();
        drawBackground();
        yPos = margin;
        drawPageHeader();
      };

      // Adds a new page if there isn't enough vertical space remaining
      const ensureSpace = (space: number) => {
        if (yPos + space > pageHeight - margin - 12) {
          addNewPage();
        }
      };

      // Normalize special/hidden characters to PDF-safe equivalents (WinAnsi Helvetica)
      const cleanText = (raw: string): string =>
        raw
          .replace(/\r\n/g, '\n').replace(/\r/g, '\n')              // normalize line endings
          .replace(/\u00A0/g, ' ')                                   // non-breaking space → space
          .replace(/\u200B|\u200C|\u200D|\uFEFF/g, '')               // zero-width chars / BOM
          .replace(/\u2022|\u25CF|\u25CB|\u25AA|\u25AB/g, '\u00b7')  // bullet variants → middle dot
          .replace(/\u2023|\u2043|\u2219/g, '-')                     // other bullets → dash
          .replace(/\u2014/g, '--')                                   // em dash → double dash
          .replace(/\u2013/g, '-')                                    // en dash → dash
          .replace(/\u201C|\u201D/g, '"')                            // curly double quotes → straight
          .replace(/\u2018|\u2019/g, "'")                            // curly single quotes → straight
          .replace(/\u2026/g, '...')                                  // ellipsis → three dots
          .replace(/\t/g, '  ');                                      // tabs → 2 spaces

      // Split commentary into PDF lines, preserving paragraph breaks and wrapping to width
      const splitCommentaryToLines = (text: string, width: number): string[] => {
        const lines: string[] = [];
        for (const para of cleanText(htmlToPdfText(text)).split('\n')) {
          const trimmed = para.trim();
          if (!trimmed) {
            lines.push(''); // blank line = paragraph break
          } else {
            const wrapped = doc.splitTextToSize(trimmed, width);
            lines.push(...(Array.isArray(wrapped) ? wrapped : [wrapped]));
          }
        }
        return lines;
      };

      // ── Page 1 ────────────────────────────────────────────────────────────────
      drawBackground();
      drawPageHeader();

      // ── Stats table ───────────────────────────────────────────────────────────
      const stats = getStatCards();
      if (stats.length > 0) {
        const monthLabel = formatMonthLabel(selectedDate);
        const prevLabel = formatPrevMonthChgLabel(selectedDate);

        if (resinType === 'PE') {
          // ── PE pivot table ─────────────────────────────────────────────────────
          // Rows = metric names, column groups = HDPE | LDPE | LLDPE | TOTAL PE
          // Each group has two sub-columns: current value | change
          type PivotCell = { value: string; change: string; isPositive: boolean; isNegative: boolean };
          const pivotData: Record<string, Record<string, PivotCell>> = {};
          const seenPolymers: string[] = [];
          const seenMetrics: string[] = [];

          stats.forEach((stat) => {
            const colonIdx = stat.title.indexOf(': ');
            if (colonIdx === -1) return;
            const polymer = stat.title.substring(0, colonIdx);
            const metric = stat.title.substring(colonIdx + 2);
            if (!seenPolymers.includes(polymer)) seenPolymers.push(polymer);
            if (!seenMetrics.includes(metric)) seenMetrics.push(metric);
            if (!pivotData[metric]) pivotData[metric] = {};
            pivotData[metric][polymer] = {
              value: stat.value,
              change: stat.change,
              isPositive: stat.isPositive,
              isNegative: stat.isNegative,
            };
          });

          // Column display order: HDPE, LDPE, LLDPE, PE → "TOTAL PE"
          const colOrder = ['HDPE', 'LDPE', 'LLDPE', 'PE'].filter((p) => seenPolymers.includes(p));
          const displayName = (p: string) => (p === 'PE' ? 'TOTAL PE' : p);

          const metricColW = 40;
          const remainingW = contentWidth - metricColW;
          const groupW = remainingW / colOrder.length;
          const subColW = groupW / 2;

          const h1 = 6.5; // polymer name header row
          const h2 = 6;   // sub-column label row
          const rowH = 6.5;
          const tableH = h1 + h2 + seenMetrics.length * rowH;

          ensureSpace(tableH + 6);
          const tableStartY = yPos;

          // Row 1: polymer group names
          doc.setFillColor(...headerRowColor);
          doc.rect(margin, tableStartY, contentWidth, h1, 'F');
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...greenColor);
          colOrder.forEach((polymer, gi) => {
            const gx = margin + metricColW + gi * groupW;
            doc.text(displayName(polymer), gx + groupW / 2, tableStartY + 4.5, { align: 'center' });
          });

          // Row 2: sub-column labels (month | prevChg)
          doc.setFillColor(35, 37, 45);
          doc.rect(margin, tableStartY + h1, contentWidth, h2, 'F');
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...greenColor);
          colOrder.forEach((_p, gi) => {
            const gx = margin + metricColW + gi * groupW;
            doc.text(monthLabel, gx + subColW / 2, tableStartY + h1 + 4.2, { align: 'center' });
            doc.text(prevLabel, gx + subColW + subColW / 2, tableStartY + h1 + 4.2, { align: 'center' });
          });

          yPos = tableStartY + h1 + h2;

          // Data rows
          seenMetrics.forEach((metric, mi) => {
            doc.setFillColor(...(mi % 2 === 0 ? evenRowColor : oddRowColor));
            doc.rect(margin, yPos, contentWidth, rowH, 'F');

            doc.setFontSize(9.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...textColor);
            doc.text(metric, margin + 2, yPos + 4.5);

            colOrder.forEach((polymer, gi) => {
              const gx = margin + metricColW + gi * groupW;
              const cell = pivotData[metric]?.[polymer];

              doc.setTextColor(...textColor);
              doc.text(cell?.value || '—', gx + subColW / 2, yPos + 4.5, { align: 'center' });

              if (cell?.isNegative) doc.setTextColor(...redColor);
              else if (cell?.isPositive) doc.setTextColor(...greenColor);
              else doc.setTextColor(...textColor);
              doc.text(cell?.change || '—', gx + subColW + subColW / 2, yPos + 4.5, { align: 'center' });
            });

            yPos += rowH;
          });

          // Outer border
          doc.setDrawColor(...jetLightColor);
          doc.setLineWidth(0.3);
          doc.rect(margin, tableStartY, contentWidth, tableH);

          // Metric column right edge
          doc.setLineWidth(0.2);
          doc.line(margin + metricColW, tableStartY, margin + metricColW, tableStartY + tableH);

          // Horizontal divider between header rows
          doc.setLineWidth(0.15);
          doc.line(margin + metricColW, tableStartY + h1, margin + contentWidth, tableStartY + h1);

          // Polymer group dividers + sub-column dividers
          colOrder.forEach((_p, gi) => {
            const gx = margin + metricColW + gi * groupW;
            if (gi > 0) {
              doc.setLineWidth(0.2);
              doc.line(gx, tableStartY, gx, tableStartY + tableH);
            }
            doc.setLineWidth(0.1);
            doc.line(gx + subColW, tableStartY + h1, gx + subColW, tableStartY + tableH);
          });

          yPos = tableStartY + tableH + 4;
        } else {
          // ── Standard 3-column table for PP / PS / PVC ──────────────────────────
          // Rows = metric names, columns = current value | change
          const metricColW = contentWidth * 0.56;
          const valueColW = contentWidth * 0.22;
          const metricX = margin;
          const valueX = margin + metricColW;
          const changeX = margin + metricColW + valueColW;

          const headerH = 8;
          const rowH = 6.5;
          const groupSize = 4;
          const numRows = stats.length;
          const tableH = headerH + numRows * rowH;

          ensureSpace(tableH + 6);
          const tableStartY = yPos;

          doc.setFillColor(...headerRowColor);
          doc.rect(margin, tableStartY, contentWidth, headerH, 'F');
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...greenColor);
          doc.text('Metric', metricX + 2, tableStartY + 5.5);
          doc.text(monthLabel, valueX + 2, tableStartY + 5.5);
          doc.text(prevLabel, changeX + 2, tableStartY + 5.5);

          yPos = tableStartY + headerH;

          stats.forEach((stat, i) => {
            doc.setFillColor(...(i % 2 === 0 ? evenRowColor : oddRowColor));
            doc.rect(margin, yPos, contentWidth, rowH, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...textColor);
            doc.text(stat.title, metricX + 2, yPos + 4.5);
            doc.text(stat.value || '—', valueX + 2, yPos + 4.5);
            if (stat.isNegative) doc.setTextColor(...redColor);
            else if (stat.isPositive) doc.setTextColor(...greenColor);
            else doc.setTextColor(...textColor);
            doc.text(stat.change || '—', changeX + 2, yPos + 4.5);
            yPos += rowH;
            if ((i + 1) % groupSize === 0 && i < numRows - 1) {
              doc.setDrawColor(...jetLightColor);
              doc.setLineWidth(0.15);
              doc.line(margin, yPos, margin + contentWidth, yPos);
            }
          });

          doc.setDrawColor(...jetLightColor);
          doc.setLineWidth(0.3);
          doc.rect(margin, tableStartY, contentWidth, tableH);
          doc.setLineWidth(0.15);
          doc.line(valueX, tableStartY, valueX, tableStartY + tableH);
          doc.line(changeX, tableStartY, changeX, tableStartY + tableH);

          yPos = tableStartY + tableH + 4;
        }
      }

      // Divider before charts section
      doc.setDrawColor(...greenColor);
      doc.setLineWidth(0.4);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      // ── Chart rendering ───────────────────────────────────────────────────────
      const chartDisplayH = 55;

      if (resinType === 'PP') {
        // PP layout: full-width commentary first, then charts 2-per-row with titles above

        // Find the PP commentary section (has .whitespace-pre-line, no canvas)
        const allSections = document.querySelectorAll('.bg-rs-off-black.rounded-lg.p-5');
        let ppTitle = '';
        let ppCommentary = '';
        for (const section of Array.from(allSections)) {
          if (!section.querySelector('canvas') && section.querySelector('.whitespace-pre-line')) {
            ppTitle = section.querySelector('h4')?.textContent?.trim() || '';
            ppCommentary = elementToPdfText(section.querySelector('.whitespace-pre-line'));
            break;
          }
        }

        // Render commentary full-width
        if (ppCommentary) {
          if (ppTitle) {
            ensureSpace(14);
            doc.setTextColor(...greenColor);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text(ppTitle, margin, yPos);
            yPos += 7;
          }
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...textColor);
          const commentaryLines = splitCommentaryToLines(ppCommentary, contentWidth);
          for (const line of commentaryLines) {
            ensureSpace(5);
            if (line) doc.text(line, margin, yPos);
            yPos += line ? 5 : 2;
          }
          yPos += 6;
        }

        // Render charts 2-per-row with title above each
        const chartIds = getChartCanvasIds();
        const chartW = (contentWidth - gutter) / 2;
        const titleRowH = 7;
        const rowH = titleRowH + chartDisplayH + 4 + 8; // title + chart bg + gap

        const renderedImgs: { title: string; img: string }[] = [];
        for (const id of chartIds) {
          const canvas = document.getElementById(id);
          if (!canvas) continue;
          const title = canvas.parentElement?.querySelector('h4')?.textContent?.trim() || '';
          const img = captureCanvasAsImage(id);
          if (img && img.length > 100) renderedImgs.push({ title, img });
        }

        for (let i = 0; i < renderedImgs.length; i++) {
          const col = i % 2;
          if (col === 0) ensureSpace(rowH);
          const xPos = margin + col * (chartW + gutter);
          const rowStartY = yPos;

          // Chart title
          doc.setTextColor(...greenColor);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.text(renderedImgs[i].title, xPos + chartW / 2, rowStartY + 5, { align: 'center' });

          // Chart image with subtle background
          doc.setFillColor(...evenRowColor);
          doc.roundedRect(xPos, rowStartY + titleRowH, chartW, chartDisplayH + 4, 2, 2, 'F');
          doc.addImage(renderedImgs[i].img, 'PNG', xPos + 2, rowStartY + titleRowH + 2, chartW - 4, chartDisplayH);

          // Advance yPos after completing each row (right column or last item)
          if (col === 1 || i === renderedImgs.length - 1) {
            yPos += rowH;
          }
        }
      } else {
        // ── All other polymers: chart on left ~54%, commentary on right ~46% ────
        const pairs = getChartCommentaryPairs();
        const chartAreaW = contentWidth * 0.54;
        const commentaryAreaW = contentWidth * 0.46 - gutter;
        const commentaryX = margin + chartAreaW + gutter;

        for (const pair of pairs) {
          const chartImg = captureCanvasAsImage(pair.canvasId);
          if (!chartImg || chartImg.length <= 100) continue;

          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'normal');
          const commentaryLines = splitCommentaryToLines(pair.text, commentaryAreaW);
          const titleH = pair.title ? 13 : 0;
          const commentaryH = titleH + commentaryLines.reduce((h, l) => h + (l ? 4.3 : 2), 0);

          const sectionH = Math.max(chartDisplayH + 4, commentaryH) + 6;
          ensureSpace(sectionH);

          const sectionStartY = yPos;

          // Chart (left half)
          doc.setFillColor(...evenRowColor);
          doc.roundedRect(margin, sectionStartY, chartAreaW, chartDisplayH + 4, 2, 2, 'F');
          doc.addImage(chartImg, 'PNG', margin + 2, sectionStartY + 2, chartAreaW - 4, chartDisplayH);

          // Commentary (right half)
          let rightY = sectionStartY;

          if (pair.title) {
            doc.setTextColor(...greenColor);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            const titleLines = doc.splitTextToSize(pair.title, commentaryAreaW);
            titleLines.forEach((line: string) => {
              doc.text(line, commentaryX, rightY + 5);
              rightY += 5;
            });
            rightY += 5.5;
          }

          doc.setTextColor(...textColor);
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'normal');
          for (const line of commentaryLines) {
            if (rightY > sectionStartY + sectionH) break;
            if (line) doc.text(line, commentaryX, rightY);
            rightY += line ? 4.3 : 2;
          }

          yPos = sectionStartY + sectionH;
        }
      }

      // ── Footer ────────────────────────────────────────────────────────────────
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      const footerY = pageHeight - 8;
      doc.text(
        'This report is for the exclusive use of RTi clients and is not to be reproduced or copied to any third party.',
        pageWidth / 2,
        footerY,
        { align: 'center' },
      );
      doc.text(
        'The prices and market information presented herein are strictly the opinion of RTi and are based on knowledge collected within the industry.',
        pageWidth / 2,
        footerY + 4,
        { align: 'center' },
      );

      try {
        if (user?.email) {
          const pageCount = doc.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(5);
            doc.setTextColor(40, 40, 40);
            doc.text(user.email, 10, doc.internal.pageSize.getHeight() - 4);
          }
        }
      } catch {}

      const pdfBlob = doc.output('blob');

      // Save to server cache so AccPdfDownload email links can serve it
      const month = selectedDate.substring(0, 7).replace('-', '');
      const typeLabel = commentaryType.charAt(0).toUpperCase() + commentaryType.slice(1);
      const cacheFileName = `ResinSmart-${resinType}-SupplyDemand-${month}-${typeLabel}-${ACC_PDF_CACHE_VERSION}.pdf`;
      try {
        await apiClient.saveAccPdf(pdfBlob, cacheFileName);
      } catch (err) {
        console.error('Failed to save ACC PDF to cache:', err);
      }

      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (error) {
      console.error('Error generating snapshot PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const isDisabled = disabled || isGenerating || !selectedDate;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(prev => !prev)}
        disabled={isDisabled}
        className={`flex items-center gap-1 px-3 py-2 rounded-md bg-rs-green !text-black font-medium transition-all hover:bg-rs-dark-green/80 disabled:cursor-not-allowed ${isDisabled ? 'opacity-50' : ''}`}
        title={`Export ${resinType} Snapshot as PDF`}
        aria-label={`Export ${resinType} Snapshot as PDF`}
      >
        {isGenerating ? (
          <RefreshCw className="w-4 h-4 animate-spin" />
        ) : (
          <Download size={20} className="flex-shrink-0" />
        )}
        <ChevronDown size={20} className={` transition-transform duration-150 ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-1 bg-rs-jet border border-rs-off-black rounded-md shadow-xl z-10 min-w-[200px] overflow-hidden">
          <button
            onClick={() => { generatePDF(); setShowDropdown(false); }}
            disabled={isDisabled}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-rs-off-white hover:bg-rs-jet-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={20} className="text-rs-green" />
            Export PDF
          </button>
        </div>
      )}
    </div>
  );
};

export default SnapshotExportButton;
